/**
 * SMSStudio.jsx - SMS Composition Component
 * 
 * Checklist Items Covered:
 * - G1: SMS Template Selector
 * - G2: SMS Preview Panel (Phone Mockup)
 * - G3: Character Count
 * - G4: Segment Count
 * - G5: SMS Recipient List
 * - G6: SMS Send Button
 * - G7: SMS Delivery Stats
 */

import React, { useState, useMemo } from 'react';
import { 
  MessageSquare, Phone, Users, Send, CheckCircle, XCircle, 
  Clock, ChevronDown, AlertTriangle, Settings, Zap
} from 'lucide-react';
import { COLORS } from './messagingStyles';

// ─── Constants ──────────────────────────────────────────────────────────────────

const SMS_CHAR_LIMIT = 160;
const SMS_EXTENDED_CHAR_LIMIT = 153; // For multi-segment messages

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: 24,
    padding: 24,
    minHeight: 'calc(100vh - 200px)',
  },
  
  // Left Column - Composition
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  
  // Template Selector Card
  selectorCard: {
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
  },
  selectorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  selectorTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  templateDropdown: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderRadius: 12,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  templateDropdownText: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  templateDropdownPlaceholder: {
    color: COLORS.textMuted,
  },
  templateList: {
    marginTop: 12,
    borderRadius: 12,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    overflow: 'hidden',
  },
  templateItem: {
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  templateItemLast: {
    borderBottom: 'none',
  },
  templateItemSelected: {
    background: COLORS.goldLight,
  },
  templateItemName: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  templateItemBody: {
    fontSize: 12,
    color: COLORS.textMuted,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  
  // Character Counter
  counterCard: {
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: `linear-gradient(180deg, rgba(198,167,90,0.08) 0%, rgba(255,255,255,0) 100%)`,
  },
  counterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  counterLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textMuted,
  },
  counterValue: {
    fontSize: 20,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  counterWarning: {
    color: '#f59e0b',
  },
  counterDanger: {
    color: COLORS.error,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    background: 'rgba(198,167,90,0.15)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    background: COLORS.gold,
    transition: 'width 0.2s ease',
  },
  progressFillWarning: {
    background: '#f59e0b',
  },
  progressFillDanger: {
    background: COLORS.error,
  },
  segmentInfo: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  segmentText: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  segmentBadge: {
    padding: '3px 8px',
    borderRadius: 6,
    background: COLORS.goldLight,
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.goldDark,
  },
  
  // Recipient Card (reused style)
  recipientCard: {
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
  },
  recipientHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  recipientTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  recipientBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    background: COLORS.goldLight,
  },
  recipientBadgeText: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.goldDark,
  },
  recipientList: {
    maxHeight: 180,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  recipientItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    background: '#FFFCF7',
    border: `1px solid ${COLORS.borderLight}`,
    cursor: 'pointer',
  },
  recipientItemSelected: {
    background: COLORS.goldLight,
    borderColor: COLORS.gold,
  },
  recipientName: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  recipientPhone: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  
  // Send Button
  sendButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '16px 24px',
    borderRadius: 14,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 6px 16px rgba(198,167,90,0.3)',
  },
  sendButtonDisabled: {
    background: '#E4DBC9',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  sendButtonText: {
    fontSize: 15,
    fontWeight: 700,
    color: '#FFFFFF',
  },
  
  // Right Column - Phone Preview
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  
  // Phone Mockup
  phoneContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  phoneMockup: {
    width: 280,
    height: 520,
    borderRadius: 36,
    border: '8px solid #1F1C17',
    background: '#FFFFFF',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
  },
  phoneNotch: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 120,
    height: 28,
    borderRadius: '0 0 16px 16px',
    background: '#1F1C17',
  },
  phoneStatusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '36px 16px 12px',
    background: '#F5F5F5',
    borderBottom: '1px solid #E5E5E5',
  },
  phoneTime: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1F1C17',
  },
  phoneCarrier: {
    fontSize: 12,
    color: '#666',
  },
  phoneContent: {
    flex: 1,
    padding: 12,
    background: '#E5E5E5',
    overflowY: 'auto',
  },
  phoneBubble: {
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: '18px 18px 18px 4px',
    background: '#FFFFFF',
    marginBottom: 8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  phoneBubbleText: {
    fontSize: 14,
    color: '#1F1C17',
    lineHeight: 1.5,
  },
  phoneTimestamp: {
    fontSize: 10,
    color: '#999',
    marginTop: 4,
    textAlign: 'right',
  },
  phoneEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: 24,
    textAlign: 'center',
  },
  phoneEmptyText: {
    fontSize: 13,
    color: '#999',
    marginTop: 12,
  },
  
  // Stats Card
  statsCard: {
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
  },
  statsTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 16,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  statItem: {
    textAlign: 'center',
    padding: 12,
    borderRadius: 12,
    background: '#FFFCF7',
  },
  statValue: {
    fontSize: 20,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  statLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: 4,
  },
  
  // Activation Notice
  activationNotice: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 18,
    border: `1px dashed ${COLORS.borderMedium}`,
    background: 'rgba(245,158,11,0.06)',
    textAlign: 'center',
  },
  activationIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: 'rgba(245,158,11,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  activationTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  activationBody: {
    fontSize: 13,
    color: COLORS.textMuted,
    maxWidth: 320,
    lineHeight: 1.5,
    marginBottom: 20,
  },
  activationBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    borderRadius: 12,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.goldDark,
  },
};

// ─── Component ──────────────────────────────────────────────────────────────────

const SMSStudio = ({
  templates = [],
  guests = [],
  selectedGuests = [],
  onToggleGuest,
  onSelectAll,
  onClearAll,
  onSend,
  isSending,
  smsStats = { sent: 0, delivered: 0, failed: 0 },
  isProviderConnected = false,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showTemplateList, setShowTemplateList] = useState(false);

  // Get selected template
  const selectedTemplate = useMemo(() => {
    return templates.find(t => t.id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  // G3: Character count calculation
  const messageText = selectedTemplate?.body || '';
  const charCount = messageText.length;
  
  // G4: Segment count calculation
  const getSegmentCount = (length) => {
    if (length === 0) return 0;
    if (length <= SMS_CHAR_LIMIT) return 1;
    return Math.ceil(length / SMS_EXTENDED_CHAR_LIMIT);
  };
  const segmentCount = getSegmentCount(charCount);
  
  const charProgress = Math.min((charCount / SMS_CHAR_LIMIT) * 100, 100);
  const isWarning = charCount > SMS_CHAR_LIMIT * 0.8 && charCount <= SMS_CHAR_LIMIT;
  const isDanger = charCount > SMS_CHAR_LIMIT;

  const isReadyToSend = selectedTemplate && selectedGuests.length > 0 && isProviderConnected;

  // If provider not connected, show activation notice
  if (!isProviderConnected) {
    return (
      <div style={{ padding: 24 }}>
        <div style={styles.activationNotice} data-testid="sms-activation-notice">
          <div style={styles.activationIcon}>
            <AlertTriangle size={24} color="#f59e0b" />
          </div>
          <div style={styles.activationTitle}>SMS Service Not Connected</div>
          <div style={styles.activationBody}>
            Connect a messaging service provider (like Telnyx or Twilio) in Settings to activate SMS templates, delivery, and activity tracking for this workspace.
          </div>
          <button style={styles.activationBtn}>
            <Settings size={16} />
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} data-testid="sms-studio">
      {/* LEFT COLUMN */}
      <div style={styles.leftColumn}>
        {/* G1: SMS Template Selector */}
        <div style={styles.selectorCard} data-testid="sms-template-selector">
          <div style={styles.selectorHeader}>
            <span style={styles.selectorTitle}>SMS Template</span>
          </div>
          
          <div
            style={styles.templateDropdown}
            onClick={() => setShowTemplateList(!showTemplateList)}
            data-testid="sms-template-dropdown"
          >
            <MessageSquare size={16} color={COLORS.goldDark} />
            <span style={{
              ...styles.templateDropdownText,
              ...(selectedTemplate ? {} : styles.templateDropdownPlaceholder),
            }}>
              {selectedTemplate?.name || 'Select a template...'}
            </span>
            <ChevronDown size={16} color={COLORS.textMuted} />
          </div>

          {showTemplateList && (
            <div style={styles.templateList}>
              {templates.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: COLORS.textMuted, fontSize: 13 }}>
                  No SMS templates available
                </div>
              ) : (
                templates.map((template, i) => (
                  <div
                    key={template.id}
                    style={{
                      ...styles.templateItem,
                      ...(i === templates.length - 1 ? styles.templateItemLast : {}),
                      ...(selectedTemplateId === template.id ? styles.templateItemSelected : {}),
                    }}
                    onClick={() => {
                      setSelectedTemplateId(template.id);
                      setShowTemplateList(false);
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTemplateId !== template.id) {
                        e.currentTarget.style.background = '#FFFCF7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTemplateId !== template.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={styles.templateItemName}>{template.name}</div>
                    <div style={styles.templateItemBody}>{template.body || 'No message content'}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* G3-G4: Character & Segment Counter */}
        <div style={styles.counterCard} data-testid="sms-counter">
          <div style={styles.counterRow}>
            <span style={styles.counterLabel}>Characters</span>
            <span style={{
              ...styles.counterValue,
              ...(isWarning ? styles.counterWarning : {}),
              ...(isDanger ? styles.counterDanger : {}),
            }}>
              {charCount} / {SMS_CHAR_LIMIT}
            </span>
          </div>
          <div style={styles.progressBar}>
            <div style={{
              ...styles.progressFill,
              width: `${charProgress}%`,
              ...(isWarning ? styles.progressFillWarning : {}),
              ...(isDanger ? styles.progressFillDanger : {}),
            }} />
          </div>
          <div style={styles.segmentInfo}>
            <span style={styles.segmentText}>
              {segmentCount === 0 ? 'No message' : 
               segmentCount === 1 ? 'Single SMS' : 
               `Multi-part message (${segmentCount} segments)`}
            </span>
            {segmentCount > 1 && (
              <span style={styles.segmentBadge} data-testid="segment-badge">
                {segmentCount} segments
              </span>
            )}
          </div>
        </div>

        {/* G5: SMS Recipient List */}
        <div style={styles.recipientCard} data-testid="sms-recipient-card">
          <div style={styles.recipientHeader}>
            <span style={styles.recipientTitle}>Recipients</span>
            <div style={styles.recipientBadge}>
              <Users size={12} color={COLORS.goldDark} />
              <span style={styles.recipientBadgeText}>
                {selectedGuests.length} selected
              </span>
            </div>
          </div>
          
          <div style={styles.recipientList}>
            {guests.filter(g => g.phone).slice(0, 8).map((guest) => {
              const isSelected = selectedGuests.includes(guest.id);
              const displayName = `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'Unknown';
              return (
                <div
                  key={guest.id}
                  style={{
                    ...styles.recipientItem,
                    ...(isSelected ? styles.recipientItemSelected : {}),
                  }}
                  onClick={() => onToggleGuest?.(guest.id)}
                >
                  <Phone size={14} color={COLORS.goldDark} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.recipientName}>{displayName}</div>
                    <div style={styles.recipientPhone}>{guest.phone}</div>
                  </div>
                  <div style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    border: `2px solid ${isSelected ? COLORS.gold : COLORS.borderMedium}`,
                    background: isSelected ? COLORS.gold : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {isSelected && <CheckCircle size={12} color="#fff" />}
                  </div>
                </div>
              );
            })}
            {guests.filter(g => g.phone).length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMuted, fontSize: 13 }}>
                No guests with phone numbers
              </div>
            )}
          </div>
        </div>

        {/* G6: SMS Send Button */}
        <button
          style={{
            ...styles.sendButton,
            ...(!isReadyToSend ? styles.sendButtonDisabled : {}),
          }}
          onClick={() => isReadyToSend && onSend?.(selectedTemplateId, selectedGuests)}
          disabled={!isReadyToSend || isSending}
          data-testid="sms-send-button"
        >
          <Send size={18} color={isReadyToSend ? '#fff' : '#A89C82'} />
          <span style={styles.sendButtonText}>
            {isSending ? 'Sending...' : 'Send SMS'}
          </span>
        </button>
      </div>

      {/* RIGHT COLUMN */}
      <div style={styles.rightColumn}>
        {/* G2: SMS Preview Panel (Phone Mockup) */}
        <div style={styles.phoneContainer} data-testid="sms-phone-preview">
          <div style={styles.phoneMockup}>
            <div style={styles.phoneNotch} />
            <div style={styles.phoneStatusBar}>
              <span style={styles.phoneTime}>9:41</span>
              <span style={styles.phoneCarrier}>Carrier</span>
            </div>
            <div style={styles.phoneContent}>
              {selectedTemplate ? (
                <div style={styles.phoneBubble}>
                  <div style={styles.phoneBubbleText}>
                    {selectedTemplate.body || 'No message content'}
                  </div>
                  <div style={styles.phoneTimestamp}>Now</div>
                </div>
              ) : (
                <div style={styles.phoneEmpty}>
                  <MessageSquare size={32} color="#CCC" />
                  <div style={styles.phoneEmptyText}>
                    Select a template to preview
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* G7: SMS Delivery Stats */}
        <div style={styles.statsCard} data-testid="sms-stats">
          <div style={styles.statsTitle}>Delivery Stats</div>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{smsStats.sent}</div>
              <div style={styles.statLabel}>Sent</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statValue, color: COLORS.success }}>{smsStats.delivered}</div>
              <div style={styles.statLabel}>Delivered</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statValue, color: COLORS.error }}>{smsStats.failed}</div>
              <div style={styles.statLabel}>Failed</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSStudio;
